import React, { useEffect, useRef } from 'react';

/**
 * LegalplusGlobe — wireframe sphere 3D sutil para el panel izquierdo del login.
 *
 * - Canvas 2D puro, cero dependencias (~3KB gzipped, no Three.js).
 * - 60fps en laptop promedio; detiene RAF cuando tab no está visible.
 * - Respeta prefers-reduced-motion (dibuja frame estático).
 * - Paleta: brand-indigo + brand-cyan sobre brand-night.
 *
 * No decora — representa la red de nodos jurídicos conectados.
 */
const LegalplusGlobe = ({ className = '', size = 560, density = 22 }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const startRef = useRef(performance.now());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const R = size * 0.38;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Fibonacci sphere — distribución uniforme de puntos
    const points = [];
    const phi = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < density * density; i++) {
      const y = 1 - (i / (density * density - 1)) * 2;
      const radius = Math.sqrt(1 - y * y);
      const theta = phi * i;
      points.push({
        x: Math.cos(theta) * radius,
        y,
        z: Math.sin(theta) * radius,
      });
    }

    // Precompute latitude/longitude lines
    const latLines = [];
    for (let lat = -60; lat <= 60; lat += 30) {
      const line = [];
      const rad = (lat * Math.PI) / 180;
      for (let lon = 0; lon <= 360; lon += 6) {
        const lrad = (lon * Math.PI) / 180;
        line.push({
          x: Math.cos(rad) * Math.cos(lrad),
          y: Math.sin(rad),
          z: Math.cos(rad) * Math.sin(lrad),
        });
      }
      latLines.push(line);
    }

    const lonLines = [];
    for (let lon = 0; lon < 360; lon += 30) {
      const line = [];
      const lrad = (lon * Math.PI) / 180;
      for (let lat = -90; lat <= 90; lat += 6) {
        const rad = (lat * Math.PI) / 180;
        line.push({
          x: Math.cos(rad) * Math.cos(lrad),
          y: Math.sin(rad),
          z: Math.cos(rad) * Math.sin(lrad),
        });
      }
      lonLines.push(line);
    }

    const rotate = (p, ay, ax) => {
      const cosY = Math.cos(ay), sinY = Math.sin(ay);
      const x1 = p.x * cosY - p.z * sinY;
      const z1 = p.x * sinY + p.z * cosY;
      const cosX = Math.cos(ax), sinX = Math.sin(ax);
      const y2 = p.y * cosX - z1 * sinX;
      const z2 = p.y * sinX + z1 * cosX;
      return { x: x1, y: y2, z: z2 };
    };

    const project = (p) => {
      const perspective = 1.6;
      const scale = perspective / (perspective - p.z);
      return {
        x: cx + p.x * R * scale,
        y: cy + p.y * R * scale,
        z: p.z,
        scale,
      };
    };

    const drawFrame = (t) => {
      ctx.clearRect(0, 0, size, size);

      const ay = t * 0.00018;
      const ax = Math.sin(t * 0.00011) * 0.25;

      // Halo radial
      const grad = ctx.createRadialGradient(cx, cy, R * 0.4, cx, cy, R * 1.8);
      grad.addColorStop(0, 'rgba(79, 125, 255, 0.10)');
      grad.addColorStop(0.55, 'rgba(92, 225, 230, 0.03)');
      grad.addColorStop(1, 'rgba(79, 125, 255, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, R * 1.8, 0, Math.PI * 2);
      ctx.fill();

      // Latitude rings
      ctx.lineWidth = 0.8;
      for (const line of latLines) {
        ctx.beginPath();
        for (let i = 0; i < line.length; i++) {
          const r = rotate(line[i], ay, ax);
          const p = project(r);
          const alpha = Math.max(0, 0.08 + r.z * 0.18);
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
          ctx.strokeStyle = `rgba(79, 125, 255, ${alpha})`;
        }
        ctx.stroke();
      }

      // Longitude meridians
      for (const line of lonLines) {
        ctx.beginPath();
        let prevFront = null;
        for (let i = 0; i < line.length; i++) {
          const r = rotate(line[i], ay, ax);
          const p = project(r);
          const front = r.z > -0.15;
          if (front !== prevFront) {
            if (front) ctx.moveTo(p.x, p.y);
            prevFront = front;
            continue;
          }
          if (front) ctx.lineTo(p.x, p.y);
        }
        ctx.strokeStyle = 'rgba(79, 125, 255, 0.14)';
        ctx.stroke();
      }

      // Nodes (fibonacci points) — los que están al frente brillan más
      for (const pt of points) {
        const r = rotate(pt, ay, ax);
        if (r.z < -0.3) continue;
        const p = project(r);
        const depth = (r.z + 1) / 2; // 0..1
        const radius = 0.6 + depth * 1.8;
        const alpha = 0.15 + depth * 0.55;
        ctx.fillStyle = `rgba(125, 161, 255, ${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Connection arcs — líneas que pulsan entre nodos frontales cercanos
      // Pulso suave basado en tiempo
      const pulse = (Math.sin(t * 0.001) + 1) / 2;
      for (let i = 0; i < points.length; i += 7) {
        const a = rotate(points[i], ay, ax);
        const b = rotate(points[(i + 3) % points.length], ay, ax);
        if (a.z < 0.1 || b.z < 0.1) continue;
        const pa = project(a);
        const pb = project(b);
        const dist = Math.hypot(pa.x - pb.x, pa.y - pb.y);
        if (dist > R * 0.55) continue;
        const alpha = (1 - dist / (R * 0.55)) * 0.18 * (0.6 + pulse * 0.4);
        ctx.strokeStyle = `rgba(92, 225, 230, ${alpha})`;
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      }

      // Core glow
      const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 0.35);
      core.addColorStop(0, 'rgba(79, 125, 255, 0.22)');
      core.addColorStop(1, 'rgba(79, 125, 255, 0)');
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(cx, cy, R * 0.35, 0, Math.PI * 2);
      ctx.fill();
    };

    if (reduce) {
      drawFrame(0);
      return () => {};
    }

    const tick = (t) => {
      drawFrame(t - startRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };

    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      } else if (!rafRef.current) {
        startRef.current = performance.now() - 16;
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelAnimationFrame(rafRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [size, density]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        width: size,
        height: size,
        display: 'block',
        filter: 'drop-shadow(0 20px 60px rgba(79, 125, 255, 0.25))',
      }}
      aria-hidden="true"
    />
  );
};

export default LegalplusGlobe;
